import axios from 'axios'

const state = {
  leads: [],
  currentLead: null,
  loading: false,
  error: null
}

// Função para transformar dados do backend para o frontend
const transformLeadData = (leadData) => {
  if (!leadData) return null;
  
  // Mapeamento de status do backend para garantir consistência
  const normalizeStatus = (status) => {
    if (!status) return 'ativo';

    const normalizedStatus = status.toLowerCase().trim();

    const statusMap = {
      'ativo': 'ativo',
      'atencao': 'atencao',
      'em_recuperacao': 'em_recuperacao',
      'recuperado': 'recuperado',
      'perdido': 'perdido',
    };

    return statusMap[normalizedStatus] || 'ativo';
  };
  
  return {
    ...leadData,
    origem: leadData.fonte || '',  // Mapeia 'fonte' do backend para 'origem' no frontend
    pontuacao: leadData.score_ia || 0,  // Mapeia 'score_ia' do backend para 'pontuacao' no frontend
    status: normalizeStatus(leadData.status), // Normaliza status para garantir consistência
    criado: leadData.criado_em,
    atualizado: leadData.atualizado_em
  };
};

// Função para transformar múltiplos leads
const transformLeadsData = (leadsData) => {
  if (!leadsData || !Array.isArray(leadsData)) return [];
  return leadsData.map(lead => transformLeadData(lead));
};

const getters = {
  allLeads: state => state.leads,
  leadById: state => id => state.leads.find(lead => lead.id === id),
  leadsByStatus: state => status => state.leads.filter(lead => lead.status === status),
  leadsByOrigin: state => origin => state.leads.filter(lead => lead.origem === origin),
  leadCount: state => state.leads.length,
  qualifiedLeadCount: state => state.leads.filter(lead => lead.status === 'recuperado').length,
  currentLead: state => state.currentLead,
  isLoading: state => state.loading,
  error: state => state.error
}

const actions = {
  async fetchLeads({ commit }) {
    commit('SET_LOADING', true)
    try {
      const response = await axios.get('/api/leads')
      const transformedLeads = transformLeadsData(response.data);
      commit('SET_LEADS', transformedLeads)
      return transformedLeads
    } catch (error) {
      commit('SET_ERROR', error.message || 'Erro ao buscar leads')
      console.error('[Leads] Erro ao buscar leads:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async fetchLead({ commit }, leadId) {
    commit('SET_LOADING', true)
    try {
      const response = await axios.get(`/api/leads/${leadId}`)
      const transformedLead = transformLeadData(response.data);
      commit('SET_CURRENT_LEAD', transformedLead)
      return transformedLead
    } catch (error) {
      commit('SET_ERROR', error.message || 'Erro ao buscar lead')
      console.error(`[Leads] Erro ao buscar lead ${leadId}:`, error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async createLead({ commit }, leadData) {
    commit('SET_LOADING', true)
    try {
      const response = await axios.post('/api/leads', leadData)
      const transformedLead = transformLeadData(response.data);
      commit('ADD_LEAD', transformedLead)
      return transformedLead
    } catch (error) {
      commit('SET_ERROR', error.message || 'Erro ao criar lead')
      console.error('[Leads] Erro ao criar lead:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async updateLead({ commit }, { id, data }) {
    commit('SET_LOADING', true)
    try {
      const response = await axios.put(`/api/leads/${id}`, data)
      const transformedLead = transformLeadData(response.data);
      commit('UPDATE_LEAD', transformedLead)
      return transformedLead
    } catch (error) {
      commit('SET_ERROR', error.message || 'Erro ao atualizar lead')
      console.error(`[Leads] Erro ao atualizar lead ${id}:`, error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async deleteLead({ commit }, leadId) {
    commit('SET_LOADING', true)
    try {
      await axios.delete(`/api/leads/${leadId}`)
      commit('REMOVE_LEAD', leadId)
      return true
    } catch (error) {
      commit('SET_ERROR', error.message || 'Erro ao excluir lead')
      console.error(`[Leads] Erro ao excluir lead ${leadId}:`, error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async qualifyLead({ commit }, leadId) {
    commit('SET_LOADING', true)
    try {
      const response = await axios.post(`/api/leads/${leadId}/qualify`)
      const transformedLead = transformLeadData(response.data);
      commit('UPDATE_LEAD', transformedLead)
      return transformedLead
    } catch (error) {
      commit('SET_ERROR', error.message || 'Erro ao qualificar lead')
      console.error(`[Leads] Erro ao qualificar lead ${leadId}:`, error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async qualifyAllLeads({ commit, dispatch }) {
    commit('SET_LOADING', true)
    try {
      const response = await axios.post('/api/leads/qualify-all')
      // Recarregar a lista de leads após qualificação em massa
      await dispatch('fetchLeads')
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message || 'Erro ao qualificar leads')
      console.error('[Leads] Erro ao qualificar todos os leads:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async getQualificationDetails({ commit }, leadId) {
    commit('SET_LOADING', true)
    try {
      const response = await axios.get(`/api/leads/${leadId}/qualification-details`)
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message || 'Erro ao obter detalhes de qualificação')
      console.error(`[Leads] Erro ao obter detalhes de qualificação do lead ${leadId}:`, error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  }
}

const mutations = {
  SET_LEADS(state, leads) {
    state.leads = leads
  },
  SET_CURRENT_LEAD(state, lead) {
    state.currentLead = lead
  },
  ADD_LEAD(state, lead) {
    state.leads.push(lead)
  },
  UPDATE_LEAD(state, updatedLead) {
    const index = state.leads.findIndex(lead => lead.id === updatedLead.id)
    if (index !== -1) {
      state.leads.splice(index, 1, updatedLead)
    }
    if (state.currentLead && state.currentLead.id === updatedLead.id) {
      state.currentLead = updatedLead
    }
  },
  REMOVE_LEAD(state, leadId) {
    state.leads = state.leads.filter(lead => lead.id !== leadId)
    if (state.currentLead && state.currentLead.id === leadId) {
      state.currentLead = null
    }
  },
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  SET_ERROR(state, error) {
    state.error = error
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
} 